import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Sparkles, Truck, Phone, Flame, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resolveImage, formatPrice, whatsappLink, phoneLink } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { StarRating } from "@/components/StarRating";

const WhatsappIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Stable pseudo "orders today" per product (social proof)
const soldToday = (id = "") => 9 + (Array.from(id).reduce((a, c) => a + c.charCodeAt(0), 0) % 31);

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const buyNow = () => {
    addItem(product);
    navigate("/finalizare");
  };
  const discount =
    product.old_price && product.old_price > product.price
      ? Math.round((1 - product.price / product.old_price) * 100)
      : 0;
  const sold = soldToday(product.id);

  return (
    <div
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-transform duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/produs/${product.id}`} className="relative block overflow-hidden bg-slate-50">
        <div className="absolute left-3 top-3 z-10 flex flex-col gap-1.5">
          {discount > 0 && (
            <span className="rounded-full bg-red-600 px-2.5 py-1 text-xs font-extrabold text-white shadow-sm" data-testid={`offer-badge-${product.id}`}>
              -{discount}%
            </span>
          )}
          <span className="flex w-fit items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-[11px] font-bold text-green-700">
            <Truck className="h-3 w-3" /> GRATUIT
          </span>
        </div>
        <img
          src={resolveImage(product.images?.[0])}
          alt={product.name}
          className="aspect-square w-full object-contain p-4 mix-blend-multiply transition-transform duration-500 ease-out group-hover:scale-[1.04]"
          loading={index < 4 ? "eager" : "lazy"}
          fetchpriority={index === 0 ? "high" : "auto"}
          decoding="async"
        />
      </Link>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <Link to={`/produs/${product.id}`}>
          <h3 className="font-heading text-base font-bold leading-snug text-slate-900 line-clamp-2 transition-colors hover:text-primary sm:text-lg">
            {product.name}
          </h3>
        </Link>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {product.rating_count > 0 && (
            <Link to="/recenzii" className="inline-flex w-fit" data-testid={`card-rating-${product.id}`} title="Vezi toate recenziile">
              <StarRating value={product.rating_avg} size={14} />
            </Link>
          )}
          {product.washes && (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Sparkles className="h-3 w-3 text-accent" /> {product.washes}{product.category === "Pachete promo" ? "+" : ""} spălări
            </span>
          )}
        </div>

        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-slate-500" data-testid={`social-proof-${product.id}`}>
          <Flame className="h-3.5 w-3.5 text-orange-500" /> {sold} comenzi azi
        </p>

        <div className="mt-auto flex items-end gap-2 pt-4">
          <span className="font-heading text-2xl font-black text-slate-900">
            {formatPrice(product.price)}
          </span>
          {product.old_price > product.price && (
            <span className="mb-1 text-sm text-slate-400 line-through">
              {formatPrice(product.old_price)}
            </span>
          )}
        </div>

        <span className="mt-2 flex w-fit items-center gap-1.5 rounded-md bg-red-50 px-2 py-1 text-[11px] font-bold text-red-600" data-testid={`stock-${product.id}`}>
          <Zap className="h-3 w-3" /> {typeof product.stock === "number" && product.stock <= 15 ? `Doar ${product.stock} buc. rămase` : "Stoc limitat"}
        </span>

        <div className="mt-4 flex flex-col gap-2">
          <Button
            onClick={buyNow}
            className="w-full rounded-full py-6 text-base font-bold shadow-sm transition-transform active:scale-[0.98]"
            data-testid={`buy-now-${product.id}`}
          >
            <ShoppingCart className="mr-2 h-5 w-5" /> Comandă acum
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <a
              href={whatsappLink(product)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 rounded-full bg-[#25D366]/10 py-2.5 text-sm font-semibold text-[#128C4A] transition-colors hover:bg-[#25D366]/20"
              data-testid={`whatsapp-${product.id}`}
            >
              <WhatsappIcon className="h-4 w-4" /> WhatsApp
            </a>
            <a
              href={phoneLink()}
              className="flex items-center justify-center gap-1.5 rounded-full bg-slate-100 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
              data-testid={`phone-${product.id}`}
            >
              <Phone className="h-4 w-4" /> Telefonic
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
